import { useState, useEffect, useReducer, useContext } from "react";

import { makeStyles } from "@material-ui/core/styles";
import { ListSubheader } from "@material-ui/core";
import Paper from "@material-ui/core/Paper";
import List from "@material-ui/core/List";

import TicketListItem from "../TicketListItem";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getSocket, joinRoom, onSocket, cleanupComponent } from "../../services/socket";

const useStyles = makeStyles((theme) => ({
  ticketsListWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },

  ticketsList: {
    flex: 1,
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    borderTop: "2px solid rgba(0, 0, 0, 0.12)",
  },

  ticketsListHeader: {
    color: "rgb(67, 83, 105)",
    zIndex: 2,
    backgroundColor: "white",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  ticketsCount: {
    fontWeight: "normal",
    color: "rgb(104, 121, 146)",
    marginLeft: "8px",
    fontSize: "14px",
  },

  noTicketsText: {
    textAlign: "center",
    color: "rgb(104, 121, 146)",
    fontSize: "14px",
    lineHeight: "1.4",
  },

  noTicketsTitle: {
    textAlign: "center",
    fontSize: "16px",
    fontWeight: "600",
    margin: "0px",
  },

  noTicketsDiv: {
    display: "flex",
    height: "100px",
    margin: 40,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_TICKETS") {
    const newTickets = action.payload;

    newTickets.forEach((ticket) => {
      const ticketIndex = state.findIndex((t) => t.id === ticket.id);
      if (ticketIndex !== -1) {
        state[ticketIndex] = ticket;
        if (ticket.unreadMessages > 0) {
          state.unshift(state.splice(ticketIndex, 1)[0]);
        }
      } else {
        state.push(ticket);
      }
    });

    return [...newTickets];
  }

  if (action.type === "RESET_UNREAD") {
    const ticketId = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      state[ticketIndex].unreadMessages = 0;
    }

    return [...state];
  }

  if (action.type === "UPDATE_TICKET") {
    const ticket = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    if (ticketIndex !== -1) {
      // Mantener información existente y actualizar con nuevos datos
      state[ticketIndex] = {
        ...state[ticketIndex],
        ...ticket,
        // Asegurar que el contacto se actualice correctamente
        contact: ticket.contact || state[ticketIndex].contact
      };
    } else {
      state.unshift(ticket);
    }

    return [...state];
  }

  if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
    const ticket = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    if (ticketIndex !== -1) {
      state[ticketIndex] = ticket;
      state.unshift(state.splice(ticketIndex, 1)[0]);
    } else {
      state.unshift(ticket);
    }

    return [...state];
  }

  if (action.type === "UPDATE_TICKET_CONTACT") {
    const contact = action.payload;
    // Buscar todos los tickets que tengan este contacto
    const updatedState = state.map(ticket => {
      if (ticket.contactId === contact.id) {
        return {
          ...ticket,
          contact: {
            ...ticket.contact,
            ...contact
          }
        };
      }
      return ticket;
    });
    return updatedState;
  }

  if (action.type === "DELETE_TICKET") {
    const ticketId = action.payload;
    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      state.splice(ticketIndex, 1);
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const TicketsList = ({
  status,
  searchParam,
  tags,
  showAll,
  selectedQueueIds,
  connections,
}) => {
  const classes = useStyles();
  const [pageNumber, setPageNumber] = useState(1);
  const [ticketsList, dispatch] = useReducer(reducer, []);
  const { user } = useContext(AuthContext);



  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [status, searchParam, dispatch, showAll, selectedQueueIds, connections]);

  const { tickets, hasMore, loading, fetchTickets } = useTickets({
    pageNumber,
    searchParam,
    tags: JSON.stringify(tags),
    status,
    showAll,
    queueIds: JSON.stringify(selectedQueueIds),
    connections: connections, // No hacer JSON.stringify aquí, se hace en el hook
  });



  useEffect(() => {
    if (!status && !searchParam) return;
    console.log(tickets)
    dispatch({
      type: "LOAD_TICKETS",
      payload: tickets,
    });
  }, [tickets, status, searchParam]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const componentId = `TicketsList-${status}`;
    const socket = getSocket(companyId);

    // ============================
    //  🔍 REGLAS DE FILTRADO
    // ============================
    const shouldUpdateTicket = (ticket) =>
      ticket.status === "closed" ||
      (
        (!ticket.userId || ticket.userId === user?.id || showAll) &&
        (!ticket.queueId || selectedQueueIds.indexOf(ticket.queueId) > -1)
      );

    // ============================
    //  🔗 JOIN AL ROOM POR STATUS
    // ============================
    const joinStatusRoom = () => {
      if (status) joinRoom(`status-${status}`);
    };

    const handleConnect = () => {
      joinStatusRoom();
    };

    // ============================
    //  🎫 HANDLER DE TICKETS (update/resolved)
    // ============================
    const handleTicketUpdate = async (data) => {
      const { ticket } = data;
      if (!ticket) return;

      // Evitar sobrescribir update optimista (dentro 500ms)
      if (ticket._optimisticUpdate || ticket._confirmedUpdate) {
        const elapsed = Date.now() - (ticket._updateTimestamp || 0);
        if (elapsed < 500) {
          console.log(`⏳ Ignorando evento duplicado (${elapsed}ms) ticket ${ticket.id}`);
          return;
        }
      }

      if (shouldUpdateTicket(ticket)) {
        dispatch({ type: "UPDATE_TICKET", payload: ticket });

        if (ticket.contact) {
          dispatch({
            type: "UPDATE_TICKET_CONTACT",
            payload: ticket.contact,
          });
        }
      } else {
        // ya no pertenece a la lista actual → eliminar
        dispatch({ type: "DELETE_TICKET", payload: ticket.id });
      }
    };

    // ============================
    //  🎫 HANDLER DE TICKETS (resolved)
    // ============================
    const handleTicketResolved = async (data) => {
      const { ticket } = data;
      if (!ticket) return;

      if (shouldUpdateTicket(ticket)) {
        dispatch({ type: "UPDATE_TICKET", payload: ticket });

        if (ticket.contact) {
          dispatch({
            type: "UPDATE_TICKET_CONTACT",
            payload: ticket.contact,
          });
        }
      } else {
        dispatch({ type: "DELETE_TICKET", payload: ticket.id });
      }
    };

    // ============================
    //  🗑️ HANDLER DE TICKETS (remove)
    // ============================
    const handleTicketRemove = (data) => {
      const ticket = data?.ticket;
      const ticketId = ticket?.id || data?.ticketId;
      if (!ticketId) return;

      dispatch({ type: "DELETE_TICKET", payload: ticketId });
    };

    // ============================
    //  💬 MENSAJES NUEVOS
    // ============================
    const handleAppMessage = (data) => {
      if (data.action === "create" && shouldUpdateTicket(data.ticket)) {
        dispatch({
          type: "UPDATE_TICKET_UNREAD_MESSAGES",
          payload: data.ticket,
        });
      }
    };

    // ============================
    //  👤 ACTUALIZACIÓN DE CONTACTO
    // ============================
    const handleContactUpdate = (data) => {
      if (data.action === "update") {
        dispatch({
          type: "UPDATE_TICKET_CONTACT",
          payload: data.contact,
        });
      }
    };

    // ============================
    //  📡 REGISTRO DE EVENTOS
    // ============================
    onSocket("connect", handleConnect, componentId);
    onSocket("ticket:update", handleTicketUpdate, componentId);
    onSocket("ticket:resolved", handleTicketResolved, componentId);
    onSocket("ticket:remove", handleTicketRemove, componentId);
    onSocket(`company:appMessage`, handleAppMessage, componentId);
    onSocket(`company:contact`, handleContactUpdate, componentId);

    // Si ya está conectado, unirse al room de inmediato
    if (socket?.connected) {
      joinStatusRoom();
    }

    // ============================
    //  🧹 CLEANUP
    // ============================
    return () => {
      cleanupComponent(componentId);
    };
  }, [status, showAll, user, selectedQueueIds, fetchTickets]);

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <div className={classes.ticketsListWrapper}>
      <Paper
        square
        name="closed"
        elevation={0}
        className={classes.ticketsList}
        onScroll={handleScroll}
      >
        <List style={{ paddingTop: 0 }}>
          {status === "open" && (
            <ListSubheader className={classes.ticketsListHeader}>
              <div>
                {i18n.t("ticketsList.assignedHeader")}
                <span className={classes.ticketsCount}>
                  {ticketsList.length}
                </span>
              </div>
            </ListSubheader>
          )}
          {status === "pending" && (
            <ListSubheader className={classes.ticketsListHeader}>
              <div>
                {i18n.t("ticketsList.pendingHeader")}
                <span className={classes.ticketsCount}>
                  {ticketsList.length}
                </span>
              </div>
            </ListSubheader>
          )}
          {ticketsList.length === 0 && !loading ? (
            <div className={classes.noTicketsDiv}>
              <span className={classes.noTicketsTitle}>
                {i18n.t("ticketsList.noTicketsTitle")}
              </span>
              <p className={classes.noTicketsText}>
                {i18n.t("ticketsList.noTicketsMessage")}
              </p>
            </div>
          ) : (
            <>
              {ticketsList.map((ticket) => (
                <TicketListItem ticket={ticket} key={ticket.id} />
              ))}
            </>
          )}
          {loading && <TicketsListSkeleton />}
        </List>
      </Paper>
    </div>
  );
};

export default TicketsList;
